import { useShallow } from 'zustand/react/shallow'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { ArrowLongLeftIcon } from '@heroicons/react/24/outline'

import { useCADPageStore } from '@/state/cad'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useStepOrderNumber } from './hooks/useStepOrderNumber'
import { cn } from '@/utils'
import { isEmptyOrSpaces } from '@/utils'
import { useAppStore } from '@/state'
import { AddPartIcon } from '@/components/icons/AddPartIcon'

export const AssemblyOrderHeader = () => {
  const { isLoading: isDocumentLoading, data: docData } = useDocumentPageQuery()

  const documentType = docData?.documentType

  const hasDismissedNoOperationsBanner = useAppStore(
    (state) => state.hasDismissedNoOperationsBanner,
  )
  const subAssemblyDocumentPageId = useCADPageStore(
    (state) => state.subAssemblyDocumentPageId,
  )
  const activeOperationStep = useCADPageStore((state) => state.operationStep)
  const isEditModeActive = useCADPageStore(
    useShallow((state) => state.isEditModeActive),
  )
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))

  const { isLoading: isLoadingSteps, steps } = useOperationSteps()

  const subStep = subAssemblyDocumentPageId
    ? steps.find((s) => s.id === subAssemblyDocumentPageId)
    : undefined

  const {
    isLoading: isLoadingOrderNumber,
    data: { orderNumber },
  } = useStepOrderNumber({
    step: subStep,
  })

  if (isDocumentLoading || isLoadingSteps || isLoadingOrderNumber) {
    return (
      <div>
        <Skeleton />
      </div>
    )
  }

  if (!subAssemblyDocumentPageId || !subStep) {
    return (
      <div className="flex gap-2 px-2 align-center">
        <div
          className={cn('text-md font-semibold', {
            'text-primary-50': activeOperationStep && !isEditModeActive,
          })}
        >
          {documentType === 'project_tracker' ? 'Notes' : 'Operations'}
        </div>
        {hasDismissedNoOperationsBanner && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <span className="mt-0.5 inline-flex shrink-0 items-center justify-center text-semantic-info-border bg-semantic-info-subtle h-5 w-5">
                  <InformationCircleIcon
                    className="w-full h-full"
                    strokeWidth={2}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent className="bg-white text-black border border-gray-300 p-2">
                <div className="max-w-[295px]">
                  <h5 className="font-bold pb-2">Add parts to Operations</h5>
                  <p>
                    Drag parts from the Assembly Tree or click
                    <span className="inline-block mx-1.5">
                      <AddPartIcon className="w-4 h-4 stroke-gray-400" />
                    </span>
                    to select parts from CAD.
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col space-y-4">
      <Button
        type="button"
        className="rounded-full flex self-start items-center space-x-2 h-6 text-xs"
        onClick={() => {
          const parentDocumentPageId = subStep.parent || null
          setCadPageState({
            subAssemblyDocumentPageId: parentDocumentPageId,
          })
        }}
      >
        <ArrowLongLeftIcon className="h-5 w-5" />
        <span>Parent Operation</span>
      </Button>
      <div className="text-sm px-2">
        <span className="font-semibold">Suboperation of </span>{' '}
        <div className="bg-primary-10 text-primary-50 rounded-lg px-2 py-1 inline-block border border-primary-20">
          <span>
            {isEmptyOrSpaces(subStep.name)
              ? `${subStep.parent ? 'Suboperation' : 'Operation'} ${orderNumber}`
              : subStep.name}
          </span>
        </div>
      </div>
    </div>
  )
}
